import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SizeRecommendation, getTitleForVariantOptions } from '@vestico/model';

import { Button } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { ProductContext } from '../../MainContext';
import { MatchButtonGlobalsContext } from '../../WidgetEventsManager';
import { NoResultsPlaceholder } from '../../components/NoResultsPlaceholder';
import { useAddToBagActions } from '../../hooks/useAddToBagActions';
import { SizeRecommendationDetail } from './SizeRecommendationDetail';
import { SizeRecommendationFineTune } from './SizeRecommendationFineTune';
import style from './SizeRecommendationTabSizeRec.module.css';

export interface SizeRecommendationTabSizeRecProps {
  sizeRecommendations: SizeRecommendation[];
  additionalProductAttributes: string[] | undefined;
  alternativeHintDisabled?: boolean;
  customDisclaimer?: string;
  showContentToggle?: boolean;
  onContentToggle?: () => void;
}

export const SizeRecommendationTabSizeRec = ({
  sizeRecommendations,
  additionalProductAttributes,
  alternativeHintDisabled,
  customDisclaimer,
  showContentToggle,
  onContentToggle,
}: SizeRecommendationTabSizeRecProps) => {
  const { t } = useTranslation();
  const [selectedRecommendationIndex, setSelectedRecommendationIndex] = useState(0);
  const { product } = useContext(ProductContext);

  const customSizeRecDisclaimer = useMemo(() => customDisclaimer?.trim(), [customDisclaimer]);

  const { addToBagEnabled, addToBagStatus, addToBagError, addToBag, reloadPage } = useAddToBagActions({ product });

  const onSelect = useCallback((index: number) => setSelectedRecommendationIndex(index), []);

  const { setMatchButtonOpen } = useContext(MatchButtonGlobalsContext);

  const onClose = useCallback(() => {
    if (showContentToggle && onContentToggle) {
      onContentToggle();
      return;
    }

    setMatchButtonOpen(false);
  }, [onContentToggle, setMatchButtonOpen, showContentToggle]);

  const selectedRecommendation: SizeRecommendation | undefined = useMemo(
    () => sizeRecommendations[selectedRecommendationIndex],
    [selectedRecommendationIndex, sizeRecommendations]
  );
  const selectedRecommendationTitle = useMemo(
    () => (selectedRecommendation ? getTitleForVariantOptions(selectedRecommendation.variantOptions) : undefined),
    [selectedRecommendation]
  );

  const onAddToBag = useCallback(() => {
    if (!selectedRecommendationTitle) {
      return;
    }

    const { available, variantUID } = selectedRecommendation;

    addToBag({ title: selectedRecommendationTitle, id: variantUID, available: available });
  }, [addToBag, selectedRecommendation, selectedRecommendationTitle]);

  const showAddToBag = useMemo(
    () => sizeRecommendations.length > 0 && addToBagEnabled && selectedRecommendation && selectedRecommendation?.available,
    [addToBagEnabled, selectedRecommendation, sizeRecommendations.length]
  );

  useEffect(() => {
    if (addToBagStatus === 'addToBagSuccess') {
      if (reloadPage && window) {
        window.location.reload();
        return;
      }
      onClose();
    }
  }, [addToBagStatus, onClose, reloadPage]);

  const addToBagStatusMarkup = useMemo(() => {
    if (!showAddToBag) {
      return null;
    }

    return addToBagError && addToBagStatus === 'addToBagError' ? (
      <div>
        <div className={clsx(globalStyle.textSmall, globalStyle.textBold, globalStyle.textColorError)}>{addToBagError}</div>
      </div>
    ) : null;
  }, [addToBagError, addToBagStatus, showAddToBag]);

  const continueButton = useMemo(() => {
    if (!showAddToBag) {
      return (
        <div className={clsx(style.btnWrapper)}>
          <Button content={t('LOC_SIZE_REC_BUTTON_CONTINUE')} onClick={onClose} />
        </div>
      );
    }

    return (
      <div className={clsx(style.btnWrapper)}>
        <Button content={t('LOC_SIZE_REC_BUTTON_ADD_SIZE_TO_BAG', { value: selectedRecommendationTitle })} onClick={onAddToBag} />
        {addToBagStatusMarkup}
      </div>
    );
  }, [addToBagStatusMarkup, onAddToBag, onClose, selectedRecommendationTitle, showAddToBag, t]);

  const isMultiRecs = sizeRecommendations.length > 1;
  const nextRect: SizeRecommendation | undefined = sizeRecommendations[1];
  const nextRecIsLarger = nextRect?.isLarger;

  const content = useMemo(() => {
    if (sizeRecommendations.length === 0) {
      return <NoResultsPlaceholder isSizeRec />;
    }

    const recommendedResult = sizeRecommendations[0];
    const { variantOptions, isLowConfidenceResult } = recommendedResult;

    const recommendedContent = (
      <div className={clsx(style.header)}>
        <div>
          <span className={clsx(style.prefix, globalStyle.textNormal, globalStyle.textFontHeader, globalStyle.textBold)}>
            {t('LOC_SIZE_REC_PREFIX')}
          </span>
        </div>
        <div>
          {variantOptions.map((option) => (
            <div key={option}>
              <span
                className={clsx(
                  globalStyle.textFontHeader,
                  globalStyle.textBold,
                  { [globalStyle.textXXLarge]: !isMultiRecs },
                  { [globalStyle.textXLarge]: isMultiRecs }
                )}
              >
                {option}
              </span>
            </div>
          ))}
        </div>
      </div>
    );

    const results =
      isMultiRecs || isLowConfidenceResult
        ? sizeRecommendations.map((sizeRecommendation, index) => {
            return (
              <SizeRecommendationDetail
                key={sizeRecommendation.variantUID}
                sizeRecommendation={sizeRecommendation}
                index={index}
                selected={selectedRecommendationIndex === index}
                onSelect={onSelect}
              />
            );
          })
        : null;

    return (
      <>
        {recommendedContent}
        <div className={clsx(style.details)}>{results}</div>
        <div className={clsx(style.text)}>
          <p className={clsx(globalStyle.textFontHeader)}>
            {isMultiRecs && !alternativeHintDisabled ? (
              <>
                {nextRecIsLarger
                  ? t('LOC_SIZE_REC_DESCRIPTION_NEXT_SIZE_LARGER', { value: getTitleForVariantOptions(nextRect.variantOptions) })
                  : t('LOC_SIZE_REC_DESCRIPTION_NEXT_SIZE_SMALLER', { value: getTitleForVariantOptions(nextRect.variantOptions) })}
              </>
            ) : null}
          </p>

          <p className={clsx(globalStyle.textFontHeader)}>
            <Trans t={t} i18nKey="LOC_SIZE_REC_DESCRIPTION_RECOMMENDATION_BASED_ON">
              <span className={globalStyle.textBold} />
            </Trans>
          </p>
          {customSizeRecDisclaimer && customSizeRecDisclaimer.length > 0 ? (
            <p className={clsx(globalStyle.textFontHeader, style.customDisclaimer)}>{customSizeRecDisclaimer}</p>
          ) : null}
        </div>
      </>
    );
  }, [
    alternativeHintDisabled,
    isMultiRecs,
    nextRecIsLarger,
    nextRect,
    onSelect,
    selectedRecommendationIndex,
    sizeRecommendations,
    t,
    customSizeRecDisclaimer,
  ]);

  const contentButton = useMemo(() => {
    if (!showContentToggle || !onContentToggle) {
      return null;
    }
    return (
      <div className={clsx(style.btnWrapper)}>
        <Button content={t('LOC_GO_TO_CONTENT_BUTTON')} onClick={onContentToggle} outline />
      </div>
    );
  }, [onContentToggle, showContentToggle, t]);

  const sizeRecFineTune = useMemo(
    () => (sizeRecommendations ? <SizeRecommendationFineTune additionalProductAttributes={additionalProductAttributes} /> : null),
    [additionalProductAttributes, sizeRecommendations]
  );

  return (
    <div className={clsx(style.root)}>
      <div className={clsx(style.content)}>
        {content}
        {continueButton}
      </div>
      {contentButton}
      {sizeRecFineTune}
    </div>
  );
};
